import React, { Suspense, lazy, useEffect } from 'react';
import './App.css';
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import { CartProvider } from './contexts/CartContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import { Toaster } from 'react-hot-toast';
import { initGA, trackPageView } from './utils/analytics';

// Lazy load components for code splitting
const Home = lazy(() => import('./pages/Home'));
const Shop = lazy(() => import('./pages/Shop'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const ShopComingSoon = lazy(() => import('./pages/ShopComingSoon'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminOrder = lazy(() => import('./pages/AdminOrder'));
const Login = lazy(() => import('./pages/Login'));
const Profile = lazy(() => import('./pages/Profile'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const TrackOrder = lazy(() => import('./pages/TrackOrder'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Component to conditionally render navbar and footer
const AppLayout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // One page view per page. Sent a moment after the route changes so the page has set its
  // title (usePageMeta); the admin dashboard is left out so your own visits don't count.
  useEffect(() => {
    if (isAdminRoute) return undefined;
    const timer = setTimeout(() => trackPageView(location.pathname + location.search, document.title), 800);
    return () => clearTimeout(timer);
  }, [location.pathname, location.search, isAdminRoute]);

  return (
    <>
      {!isAdminRoute && <Navbar />}
      {children}
      {!isAdminRoute && <Footer />}
    </>
  );
};

// The shop is behind the "coming soon" access code until launch. To open it to everyone,
// set REACT_APP_SHOP_OPEN=true in Netlify (Site configuration > Environment variables) and
// redeploy; the sitemap then lists the shop and products too (scripts/generate-sitemap.js).
const SHOP_OPEN = process.env.REACT_APP_SHOP_OPEN === 'true';

const hasShopAccess = () => {
  if (SHOP_OPEN) return true;
  try {
    return localStorage.getItem('shopAccessGranted') === 'true';
  } catch {
    return false;
  }
};

const ShopWithProtection = () => (hasShopAccess() ? <Shop /> : <ShopComingSoon />);
const CategoryPageWithProtection = () => (hasShopAccess() ? <CategoryPage /> : <ShopComingSoon />);
const ProductDetailWithProtection = () => (hasShopAccess() ? <ProductDetail /> : <ShopComingSoon />);

function App() {
  // Initialize Google Analytics
  useEffect(() => {
    // REACT_APP_GA_MEASUREMENT_ID is set in Netlify's environment variables
    initGA(process.env.REACT_APP_GA_MEASUREMENT_ID);
  }, []);

  return (
    <ErrorBoundary>
      <CartProvider>
        <Router>
          <ScrollToTop />
          <AppLayout>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/shop" element={<ShopWithProtection />} />
                <Route path="/shop/category/:categorySlug" element={<CategoryPageWithProtection />} />
                <Route path="/shop/:id" element={<ProductDetailWithProtection />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/order-confirmation" element={<OrderConfirmation />} />
                <Route path="/login" element={<Login />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/admin" element={<AdminLogin />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/orders/:id" element={<AdminOrder />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/track-order" element={<TrackOrder />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AppLayout>
        </Router>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#10B981',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#EF4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </CartProvider>
    </ErrorBoundary>
  );
}

export default App;